import { AfterViewInit, Directive, ElementRef, OnDestroy } from '@angular/core';

import {
  filter,
  fromEvent,
  Subscription,
} from 'rxjs';

import { FrontendModalService } from '@tablesready/frontend-modal';
import { FrontendKeyboardService } from '@tablesready/frontend-keyboard';
import { Capacitor } from '@capacitor/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Directive({
  selector: '[modalContent]',
  standalone: false,
})
export class FrontendModalContentDirective implements AfterViewInit, OnDestroy {
  browser = '';

  private windowHeight = window.innerHeight;
  private headerHeight?: number;
  private footerHeight?: number;
  private resizeObserver?: ResizeObserver;
  private isKeyboardNowOpen = false;
  private readonly subscription = new Subscription();

  constructor(
    private readonly el: ElementRef,
    private readonly service: FrontendModalService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly keyboardService: FrontendKeyboardService,
  ) {
    this.browser = this.deviceDetectorService.browser;
  }

  ngAfterViewInit(): void {
    const footerSub = this.service.footerHeightSet$
      .pipe(filter(data => data !== null))
      .subscribe((footer) => {
        this.footerHeight = footer;
        this.setContentHeight();
      });
    this.subscription.add(footerSub);

    const orientationSub = this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
      Breakpoints.WebPortrait,
      Breakpoints.WebLandscape,
    ])
      .subscribe(() => {
        this.windowHeight = window.innerHeight;
      });
    this.subscription.add(orientationSub);

    const headerSub = this.service.headerHeightSet$
      .pipe(filter(data => data !== null))
      .subscribe((header) => {
        this.headerHeight = header;
        this.setContentHeight();
      });
    this.subscription.add(headerSub);

    this.observeChildrenResize();

    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const keyboardSub = this.keyboardService.keyboardVisible$
      .subscribe((visible) => {
        this.isKeyboardNowOpen = visible;
        this.setContentHeight();
      });
    this.subscription.add(keyboardSub);

    const resizeSub = fromEvent(window, 'resize')
      .subscribe(() => {
        this.setContentHeight();
      });
    this.subscription.add(resizeSub);
  }

  get isSafari(): boolean {
    return this.browser === 'Safari';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  getAggregateHeight(element: HTMLElement): number {
    if (!element) return 0;

    let totalHeight = 0;
    const rootStyle = window.getComputedStyle(element);
    const paddingTop = parseFloat(rootStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(rootStyle.paddingBottom) || 0;

    for (let i = 0; i < element.children.length; i++) {
      const child = element.children[i] as HTMLElement;

      if (child instanceof HTMLElement) {
        const childHeight = child.offsetHeight || 0;

        const style = window.getComputedStyle(child);
        const marginTop = parseFloat(style.marginTop) || 0;
        const marginBottom = parseFloat(style.marginBottom) || 0;

        totalHeight += childHeight + marginTop + marginBottom;
      }
    }

    return totalHeight + paddingTop + paddingBottom;
  }

  setContentHeight(): void {
    if (!this.headerHeight || !this.footerHeight) {
      return;
    }

    this.calc();
  }

  private calc(): void {
    const ionModal = this.findIonModalElement(this.el.nativeElement);
    const headerHeight = this.getOwnHeight(ionModal, '[modalHeader]', this.headerHeight);
    const footerHeight = this.getOwnHeight(ionModal, '[modalFooter]', this.footerHeight);

    let modalTopPadding = '2rem';
    let modalBottomPadding = '1rem';
    let actualContentHeight = this.getAggregateHeight(this.el.nativeElement);
    let ionModalHeight = actualContentHeight + headerHeight + footerHeight;
    let viewportHeight = window.innerHeight;

    if (this.isSafari && !Capacitor.isNativePlatform()) {
      viewportHeight = this.windowHeight;
    }

    this.el.nativeElement.style.overflowY = 'auto';
    if (ionModalHeight > viewportHeight) {
      actualContentHeight = viewportHeight - (headerHeight + footerHeight);
      ionModalHeight = actualContentHeight + headerHeight + footerHeight;
    }

    if (Capacitor.isNativePlatform()) {
      modalTopPadding = 'env(safe-area-inset-top)';
      modalBottomPadding = 'env(safe-area-inset-bottom)';
    }

    this.el.nativeElement.style.height = `${actualContentHeight}px`;
    if (!ionModal) {
      return;
    }

    ionModal.style.display = 'flex';
    ionModal.style.alignItems = 'flex-start';
    ionModal.style.paddingTop = modalTopPadding;
    ionModal.style.boxSizing = 'border-box';
    ionModal.style.setProperty('--height', `calc(${ionModalHeight}px)`);
    ionModal.style.setProperty(
      '--max-height',
      `calc(${viewportHeight}px - ${modalTopPadding} - ${modalBottomPadding})`
    );
  }

  private observeChildrenResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.calc();
    });

    const parent = this.el.nativeElement as HTMLElement;
    for (const child of Array.from(parent.children)) {
      this.resizeObserver.observe(child);
    }
  }

  private getOwnHeight(ionModal: HTMLElement | undefined, selector: string, fallback?: number): number {
    const element = ionModal?.querySelector<HTMLElement>(selector);
    if (!element) {
      return fallback || 0;
    }

    return element.offsetHeight;
  }

  private findIonModalElement(nativeElement: HTMLElement): HTMLElement | undefined {
    let currentElement: HTMLElement | null = nativeElement;

    while (currentElement && currentElement !== document.body) {
      if (currentElement.tagName.toLowerCase() === 'ion-modal') {
        return currentElement;
      }
      currentElement = currentElement.parentElement;
    }

    return undefined;
  }
}
